import { AppState } from "..";
import { BizzeySetting } from "../../../../packs/widget_shim";
import {
  PublicLocale,
  Widget,
  WidgetClientEventType,
  WidgetCounters,
  WidgetSession,
  WidgetSpaceType,
} from "../../../types";
import { MessageHelper } from "../../helpers";
import { isLauncherVisibleOnInitOrClose } from "./reducer";
import {
  ADD_CLOSE_CALLBACK,
  ADD_HIDE_LAUNCHER_CALLBACK,
  ADD_OPEN_CALLBACK,
  ADD_SHOW_LAUNCHER_CALLBACK,
  ApplicationActionTypes,
  ApplicationState,
  MAXIMIZE_WINDOW,
  MINIMIZE_WINDOW,
  SET_APPLICATION_STATE,
  SET_MESSENGER_FRAME,
  SET_HOST_WINDOW_SIZE,
  SET_WINDOW_MAXIMIZED,
  SET_BIZZEY_SETTING,
  SET_WIDGET,
  SET_WIDGET_SESSION,
  SET_LOCALE,
  SET_WIDGET_COUNTERS,
  ADD_READY_CALLBACK,
} from "./types";

export function setApplicationState(
  state: ApplicationState
): ApplicationActionTypes {
  return {
    type: SET_APPLICATION_STATE,
    state: state,
  };
}

export function setBizzeySetting(
  bizzeySetting: BizzeySetting
): ApplicationActionTypes {
  return {
    type: SET_BIZZEY_SETTING,
    bizzeySetting: bizzeySetting,
  };
}

export function setLocale(locale: PublicLocale): ApplicationActionTypes {
  return {
    type: SET_LOCALE,
    locale: locale,
  };
}

export function setWidget(widget: Widget): ApplicationActionTypes {
  return {
    type: SET_WIDGET,
    widget: widget,
  };
}

export function setWidgetSession(
  session: WidgetSession
): ApplicationActionTypes {
  return {
    type: SET_WIDGET_SESSION,
    session: session,
  };
}

export function setMessengerFrame(messengerFrame: HTMLIFrameElement) {
  return {
    type: SET_MESSENGER_FRAME,
    messengerFrame: messengerFrame,
  };
}

export function showLauncher() {
  return (dispatch, getState) => {
    const { application } = getState();
    const { onShowLauncherCallbacks } = application;

    dispatch(setApplicationState({ ...application, launcherVisible: true }));

    // Call all the callbacks
    onShowLauncherCallbacks.forEach((callback) => callback());
  };
}

export function hideLauncher() {
  return (dispatch, getState) => {
    const { application } = getState();
    const { onShowLauncherCallbacks } = application;

    dispatch(
      setApplicationState({
        ...application,
        launcherVisible: false,
        open: false,
      })
    );

    // Call all the callbacks
    onShowLauncherCallbacks.forEach((callback) => callback());
  };
}

export function open() {
  return (dispatch, getState) => {
    const { application } = getState();
    const { onOpenCallbacks } = application;

    dispatch(
      setApplicationState({ ...application, launcherVisible: true, open: true })
    );

    // Call all the callbacks
    onOpenCallbacks.forEach((callback) => callback());
  };
}

export function close() {
  return (dispatch, getState) => {
    const { application }: AppState = getState();
    const { bizzeySetting, widget, onCloseCallbacks } = application;

    dispatch(
      setApplicationState({
        ...application,
        open: false,
        launcherVisible: isLauncherVisibleOnInitOrClose(bizzeySetting, widget),
      })
    );

    // Call all the callbacks
    onCloseCallbacks.forEach((callback) => callback());
  };
}

export function toggleOpen() {
  return (dispatch, getState) => {
    const { application }: AppState = getState();

    if (application.open) {
      dispatch(close());
    } else {
      dispatch(open());
    }
  };
}

export function addShowLauncherCallback(callback: () => void) {
  return {
    type: ADD_SHOW_LAUNCHER_CALLBACK,
    callback: callback,
  };
}

export function addHideLauncherCallback(callback: () => void) {
  return {
    type: ADD_HIDE_LAUNCHER_CALLBACK,
    callback: callback,
  };
}

export function addReadyCallback(callback: () => void) {
  return {
    type: ADD_READY_CALLBACK,
    callback: callback,
  };
}

export function addOpenCallback(callback: () => void) {
  return {
    type: ADD_OPEN_CALLBACK,
    callback: callback,
  };
}

export function addCloseCallback(callback: () => void) {
  return {
    type: ADD_CLOSE_CALLBACK,
    callback: callback,
  };
}

export function navigate(space: WidgetSpaceType) {
  return (dispatch, getState) => {
    dispatch(
      setApplicationState({
        ...getState().application,
        open: true,
        launcherVisible: true,
      })
    );

    MessageHelper.sendMessengerFrameMessage({
      type: WidgetClientEventType.NAVIGATE,
      space: space,
    });
  };
}

export function maximizeWindow() {
  return (dispatch, getState) => {
    dispatch({ type: MAXIMIZE_WINDOW });

    MessageHelper.sendMessengerFrameMessage({
      type: WidgetClientEventType.MAXIMIZE_WINDOW,
    });
  };
}

export function minimizeWindow() {
  return (dispatch, getState) => {
    dispatch({ type: MINIMIZE_WINDOW });

    MessageHelper.sendMessengerFrameMessage({
      type: WidgetClientEventType.MINIMIZE_WINDOW,
    });
  };
}

export function toggleWindowSize() {
  return (dispatch, getState) => {
    const {
      application: { maximized },
    } = getState();

    if (maximized) {
      dispatch({ type: MINIMIZE_WINDOW });

      MessageHelper.sendMessengerFrameMessage({
        type: WidgetClientEventType.MINIMIZE_WINDOW,
      });
    } else {
      dispatch({ type: MAXIMIZE_WINDOW });

      MessageHelper.sendMessengerFrameMessage({
        type: WidgetClientEventType.MAXIMIZE_WINDOW,
      });
    }
  };
}

export function setHostWindowSize(
  width: number,
  height: number
): ApplicationActionTypes {
  return {
    type: SET_HOST_WINDOW_SIZE,
    width: width,
    height: height,
  };
}

export function setWindowMaximized(maximized: boolean): ApplicationActionTypes {
  return {
    type: SET_WINDOW_MAXIMIZED,
    maximized: maximized,
  };
}

export function setWidgetCounters(
  counters: WidgetCounters
): ApplicationActionTypes {
  return {
    type: SET_WIDGET_COUNTERS,
    counters: counters,
  };
}
